import { ArrowRight, Check } from "lucide-react";

const STAGES = ["Attract & Hire", "Onboard", "Serve", "Develop & Retain", "Offboard"];

const Frame = ({ title, note, children }) => (
  <div>
    <div className="flex items-baseline gap-3 mb-3">
      <span className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-[#E8AA68]">{title}</span>
    </div>
    <div className="rounded-2xl bg-[#211307] text-[#F7F5F0] p-6 lg:p-7">
      <h4 className="font-display text-xl font-medium mb-1">Hire-to-Retire</h4>
      <p className="text-[13px] text-white/70 mb-6 max-w-2xl">Transform the complete employee lifecycle with AI agents working across every stage of the employee journey.</p>
      {children}
    </div>
    <p className="text-[12px] text-white/45 mt-2">{note}</p>
  </div>
);

/* A · Connected pills (current) */
const StepPills = () => (
  <div className="flex flex-wrap items-center gap-2.5">
    {STAGES.map((s, i) => (
      <div key={s} className="flex items-center gap-2.5">
        <span className="flex items-center gap-2 rounded-full bg-white/8 border border-white/15 px-3.5 py-2">
          <span className="font-mono2 text-[10px] text-[#E8AA68]">{String(i + 1).padStart(2, "0")}</span>
          <span className="text-[12.5px] font-semibold">{s}</span>
        </span>
        {i < STAGES.length - 1 && <ArrowRight size={14} className="text-[#E8AA68]" />}
      </div>
    ))}
  </div>
);

/* F · Connected pills, borderless */
const StepPillsNoBorder = () => (
  <div className="flex flex-wrap items-center gap-2.5">
    {STAGES.map((s, i) => (
      <div key={s} className="flex items-center gap-2.5">
        <span className="flex items-center gap-2 rounded-full bg-white/8 px-3.5 py-2">
          <span className="font-mono2 text-[10px] text-[#E8AA68]">{String(i + 1).padStart(2, "0")}</span>
          <span className="text-[12.5px] font-semibold">{s}</span>
        </span>
        {i < STAGES.length - 1 && <ArrowRight size={14} className="text-[#E8AA68]" />}
      </div>
    ))}
  </div>
);

/* B · Node stepper on a line */
const StepStepper = () => (
  <div className="relative flex justify-between gap-2 pt-1">
    <div className="absolute top-[14px] left-4 right-4 h-px bg-gradient-to-r from-[#E8AA68]/30 via-[#E8AA68]/60 to-[#E8AA68]/30" />
    {STAGES.map((s, i) => (
      <div key={s} className="relative flex flex-col items-center text-center flex-1">
        <span className="w-7 h-7 rounded-full bg-[#E8AA68] text-[#211307] text-[12px] font-bold flex items-center justify-center z-10">{i + 1}</span>
        <span className="text-[12px] mt-2.5 text-white/80 max-w-[100px] leading-snug">{s}</span>
      </div>
    ))}
  </div>
);

/* C · Chevron flow */
const StepChevrons = () => (
  <div className="flex flex-wrap">
    {STAGES.map((s, i) => (
      <div
        key={s}
        className={`text-[12.5px] font-semibold py-3 pl-6 pr-7 ${i % 2 === 0 ? "bg-white/[0.07]" : "bg-white/[0.12]"} ${i === 0 ? "rounded-l-lg" : "-ml-3"}`}
        style={{
          clipPath:
            i === 0
              ? "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)"
              : "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)",
        }}
      >
        <span className="text-[#E8AA68] mr-1.5 font-mono2 text-[10px]">{String(i + 1).padStart(2, "0")}</span>
        {s}
      </div>
    ))}
  </div>
);

/* D · Numbered columns with dividers */
const StepColumns = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
    {STAGES.map((s, i) => (
      <div key={s} className={`relative pl-4 ${i > 0 ? "border-l border-white/12" : ""}`}>
        <span className="font-display text-3xl font-semibold text-[#E8AA68] leading-none">{String(i + 1).padStart(2, "0")}</span>
        <p className="text-[13px] font-semibold mt-2 leading-snug">{s}</p>
      </div>
    ))}
  </div>
);

/* E · Milestone track with dots + gold connectors */
const StepMilestones = () => (
  <div className="flex items-center">
    {STAGES.map((s, i) => (
      <div key={s} className="flex items-center flex-1 last:flex-none">
        <div className="flex flex-col items-start">
          <span className="w-3 h-3 rounded-full bg-[#E8AA68] ring-4 ring-[#E8AA68]/15" />
          <span className="text-[12px] font-semibold mt-2 whitespace-nowrap">{s}</span>
        </div>
        {i < STAGES.length - 1 && <span className="h-px flex-1 bg-white/20 mx-3 mb-6" />}
      </div>
    ))}
  </div>
);

const OPTIONS = [
  { id: "pills", title: "Option A · Connected Pills (current)", comp: StepPills, note: "Numbered pills joined by arrows — compact and clear." },
  { id: "stepper", title: "Option B · Node Stepper", comp: StepStepper, note: "Numbered nodes on a gold progress line with labels beneath — classic process stepper." },
  { id: "chevrons", title: "Option C · Chevron Flow", comp: StepChevrons, note: "Arrow-shaped segments that flow left to right — reads as a directional process." },
  { id: "columns", title: "Option D · Numbered Columns", comp: StepColumns, note: "Big gold numbers in divided columns — editorial and confident." },
  { id: "milestones", title: "Option E · Milestone Track", comp: StepMilestones, note: "Dots on a connecting track — a lightweight timeline feel." },
  { id: "pills-noborder", title: "Option F · Connected Pills, Borderless", comp: StepPillsNoBorder, note: "Same as Option A but with no pill border — softer, cleaner look." },
];

export default function JourneyOptions() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Hire-to-Retire · Step Designs</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">Pick a design for the journey steps</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Each option shows the five Hire-to-Retire stages inside the dark use-case block. Tell me the letter and I&rsquo;ll apply it in the live section.</p>
        <div className="mt-10 space-y-10">
          {OPTIONS.map((o) => {
            const C = o.comp;
            return <Frame key={o.id} title={o.title} note={o.note}><C /></Frame>;
          })}
        </div>
      </div>
    </div>
  );
}
